import { normaliseShopifyPrice } from '@utils/prices';
import { consolidateFrames } from '@utils/product';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';

import { useDelegateCash } from '../components/Web3/Web3Provider/useDelegateCash';
import { useWeb3Cache } from '../components/Web3/Web3Provider/useWeb3Cache';
import { DEFAULT_CURRENCY_CODE } from '../constants/locations';
import { AML_LIMIT } from '../constants/product';
import { useAuth } from '../providers';
import {
  AuthErrorResponse,
  AuthGateway,
  GenerateMultipassTokenResponse,
} from '../types/Auth';
import { ProductGateway } from '../types/AvantArteProduct';
import { GeneratedTaxType } from '../types/generated';
import { GiftCardCheckoutPayload } from '../types/GiftCard';
import { CurrencyCode } from '../types/Locations';
import { Frame, FrameOption, FrameOptionConsolidated } from '../types/Products';
import type {
  AttributeInput,
  CheckoutGateway,
  CollectionSetProduct,
  ProductPurchaseDetails,
  ProductPurchasingController,
  ProductsGateway,
  ProductUnlockGateway,
  UnlockMessage,
} from '../types/Purchasing';
import { TrackUnlockCode } from '../utils/analytics';
import { getDispatchDate } from '../utils/dispatch';
import { framedByDefault } from '../utils/frames';
import { useCalculateOrderPricing } from './useCalculateOrderPricing';
import { useCountryCode } from './useCountryCode';
import { useCurrency } from './useCurrency';
import { useOnBfCacheLoad } from './useOnBfCacheLoad';
import { ReleaseGatingController } from './useReleaseGating';
import { getCookie } from '@utils/cookies';

export type Options<
  TProductIdentifier,
  TProduct extends ProductPurchaseDetails,
> = {
  identifier: TProductIdentifier;
  release?: string;
  productsGateway: ProductsGateway<TProductIdentifier, TProduct>;
  checkoutGateway: CheckoutGateway<TProduct, GiftCardCheckoutPayload>;
  authGateway?: AuthGateway;
  unlockGateway: ProductUnlockGateway<TProductIdentifier>;
  releaseGating?: ReleaseGatingController;
  releaseType?: StoryblokReleaseType;
  aaProductsGateway: ProductGateway;
  variantIdentifier?: string;
  isPrivateSale?: boolean;
  privateLinkIdentifier?: string;
  preview?: boolean;
  productDetails: TProduct | null;
};

export function useProductPurchasing<
  TProductIdentifier,
  TProduct extends ProductPurchaseDetails,
>(
  options: Options<TProductIdentifier, TProduct>,
): ProductPurchasingController<TProduct> {
  const [currency] = useCurrency();
  const [countryCode] = useCountryCode();
  const [product, setProduct] = useState<TProduct | null>(
    options.productDetails || null,
  );
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [frame, setFrame] = useState<Frame | null>(null);
  const [frameOptions, setFrameOptions] = useState<Array<FrameOption> | null>(
    null,
  );
  const [frameOptionsConsolidated, setFrameOptionsConsolidated] =
    useState<Array<FrameOptionConsolidated> | null>(null);

  const [unlocked, setUnlocked] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [requiresUnlock, setRequiresUnlocking] = useState(false);
  const [unlockCode, setUnlockCode] = useState<string | undefined>(undefined);
  const [unlockMessage, setUnlockMessage] = useState<UnlockMessage | undefined>(
    undefined,
  );
  const [quantity, setProductQuantity] = useState(1);
  const [collectionSetProducts, setCollectionSetProducts] = useState<
    Array<CollectionSetProduct>
  >([]);

  const [collectionSetDescription, setCollectionSetDescription] = useState<
    string | null
  >(null);
  const [collectionSetImage, setCollectionSetImage] = useState<string | null>(
    null,
  );

  const { isLoggedIn } = useAuth();
  const router = useRouter();

  const items = useMemo(() => {
    const selected = [];
    if (product?.shopifyVariantId) {
      selected.push({
        variantId: product.shopifyVariantId,
        quantity,
      });
    }

    if (frame) {
      frame.variant.consolidatedVariants.forEach((variant) => {
        selected.push({
          variantId: variant.shopifyFrameVariantId,
          quantity: variant.quantity,
        });
      });
    }

    return selected;
  }, [frame, product?.shopifyVariantId, quantity]);

  const orderPriceCalculation = useCalculateOrderPricing({
    items,
    forceCurrency: DEFAULT_CURRENCY_CODE,
  });
  const totals = orderPriceCalculation.calculation;

  const requiresGating =
    !!options.releaseGating && !!options.releaseGating.requiresNftGating;

  const prevId = usePrevious(options.identifier);

  const { walletAddress } = useWeb3Cache();
  const delegateCash = useDelegateCash();

  const canPurchase =
    !requiresGating ||
    (!!options.releaseGating && !!options.releaseGating.passedGating);

  // whenever the quantity of a single product, or one of the products in
  // the collection set changes, we need to recalculate the frame options
  useEffect(() => {
    if (!frameOptions) {
      return;
    }

    // collection sets only
    if (collectionSetProducts.length) {
      const selection = collectionSetProducts
        .filter(({ selected }) => selected)
        .map((selectedProduct) => ({
          id: selectedProduct.shopifyId,
          quantity: selectedProduct.quantity,
        }));
      const consolidated = consolidateFrames({
        frameOptions,
        selection,
        compositeImageProductShopifyId: product?.shopifyId ?? '',
      });

      setFrameOptionsConsolidated(consolidated);

      return;
    }

    // regular purchase of a single product
    const consolidated = consolidateFrames({
      frameOptions,
      selection: [
        {
          id: product?.shopifyId ?? '',
          quantity,
        },
      ],
      compositeImageProductShopifyId: product?.shopifyId ?? '',
    });

    setFrameOptionsConsolidated(consolidated);
  }, [quantity, collectionSetProducts, frameOptions, product?.shopifyId]);

  const updatePurchaseInfo = useCallback(async () => {
    setLoading(true);

    // Clear frame when switching products
    if (prevId !== options.identifier) setFrame(null);

    const purchasingDetails =
      await options.productsGateway.getProductPurchaseDetails({
        identifier: options.identifier,
        releaseName: options.release || null,
        variantIdentifier: options.variantIdentifier,
      });

    if (purchasingDetails) {
      setProduct(purchasingDetails);

      if (purchasingDetails.requiresUnlockCode) {
        setRequiresUnlocking(purchasingDetails.requiresUnlockCode);
      }

      setFrameOptions(purchasingDetails.frames);

      if (purchasingDetails.collectionSet) {
        setCollectionSetProducts(
          options.isPrivateSale
            ? purchasingDetails.collectionSet
            : purchasingDetails.collectionSet.filter(
                (release) => !release.isSoldOut,
              ),
        );
      }

      if (purchasingDetails.collectoinSetDescription) {
        setCollectionSetDescription(purchasingDetails.collectoinSetDescription);
      }

      if (purchasingDetails.collectoinSetImage) {
        setCollectionSetImage(purchasingDetails.collectoinSetImage);
      }
    }
    setLoading(false);

    return void 0;
  }, [
    options.identifier,
    options.release,
    options.variantIdentifier,
    options.productsGateway,
    prevId,
  ]);

  useEffect(() => {
    updatePurchaseInfo();
  }, [updatePurchaseInfo]);

  useOnBfCacheLoad(() => {
    setCheckoutLoading(false);
  });

  const getTaxInformationAttributes = async () => {
    const attributes: AttributeInput[] = [];
    const taxReponse = await options.aaProductsGateway.getTaxesForCountryCode(
      countryCode,
    );

    attributes.push({ key: 'country_code', value: countryCode });
    if (taxReponse.tax && taxReponse.tax.taxType === GeneratedTaxType.Vat)
      attributes.push({
        key: 'vat_rate',
        value: taxReponse.tax.vatRate.toString(),
      });

    return attributes;
  };

  const getUserInformationAttributes = () => {
    const attributes: AttributeInput[] = [];
    if (isLoggedIn) {
      attributes.push({
        key: 'user_state',
        value: 'LOGGED_IN',
      });
    } else {
      attributes.push({
        key: 'user_state',
        value: 'LOGGED_OUT',
      });
    }
    return attributes;
  };

  const getCustomAttributes = async () => {
    const customAttributes: AttributeInput[] = [];

    // release gating
    if (options.releaseGating) {
      customAttributes.push(
        ...options.releaseGating.createCustomOrderAttributes(),
      );
    }

    // private sales
    if (options.isPrivateSale && options.privateLinkIdentifier) {
      customAttributes.push({
        key: 'private_link_id',
        value: options.privateLinkIdentifier,
      });
    }

    // user information
    customAttributes.push(...getUserInformationAttributes());

    // tax information
    const taxAttributes = await getTaxInformationAttributes();
    customAttributes.push(...taxAttributes);

    const metaPixelFbc = getCookie('_fbc');
    const metaPixelFbcValue = metaPixelFbc?.split('.').at(-1);

    if (metaPixelFbcValue) {
      customAttributes.push({
        key: 'custom_fbc',
        value: metaPixelFbcValue,
      });
    }

    return customAttributes;
  };

  const goToCheckout = async () => {
    if (product === null) return;
    setCheckoutLoading(true);

    const customAttributes = await getCustomAttributes();

    if (options.releaseGating?.requiresNftGating && isLoggedIn) {
      customAttributes.push(
        (
          await options.authGateway!.generateAvantPassToken({
            walletAddress,
            delegateCashVault: delegateCash.activeVaultAddress,
          })
        ).customAttribute,
      );
    }

    const collectionSetProductsFiltered = collectionSetProducts.filter(
      ({ selected }) => selected,
    );

    options.checkoutGateway
      .checkoutWithProduct({
        collectionSetProducts: collectionSetProductsFiltered,
        currency,
        customAttributes,
        frame,
        product,
        quantity,
        unlockCode,
      })
      .then((result) => {
        (async () => {
          let redirectURL = result;
          if (isLoggedIn && !options.isPrivateSale) {
            const multipassTokenResponse =
              await options.authGateway!.generateShopifyMultipassToken(result);
            if (!(multipassTokenResponse as AuthErrorResponse).error) {
              redirectURL = `${process.env.NEXT_PUBLIC_MULTIPASS_URL}${
                (multipassTokenResponse as GenerateMultipassTokenResponse).token
              }`;
            }
          }

          if (
            totals?.totalPrice &&
            normaliseShopifyPrice(
              totals.totalPrice,
              totals.currencyCode as CurrencyCode,
            ) > AML_LIMIT
          ) {
            const searchParams = new URLSearchParams({
              redirect: redirectURL,
            });
            router.push({
              pathname: '/aml',
              search: `?${searchParams.toString()}`,
            });
          } else {
            router.push(redirectURL);
          }
        })();
      });
  };

  const unlockProduct = async (code: string) => {
    if (product === null) return;

    setUnlockLoading(true);
    setUnlockCode(code);

    const state = await options.unlockGateway.unlockProduct(
      options.identifier,
      options.release,
      code,
    );

    const success = state.codeValid && !state.codeExpired;

    setUnlocked(success);
    setUnlockLoading(false);

    TrackUnlockCode({
      type: state.codeValid ? 'correct' : 'incorrect',
      productTitle: product.name,
      productPrice: product.price,
      productCurrencyCode: product.currency,
      code,
      expired: state.codeExpired,
    });

    if (success) setUnlockMessage('UNLOCK_SUCCESS');
    else if (!state.codeValid) setUnlockMessage('UNLOCK_FAILED');
    else setUnlockMessage('UNLOCK_EXPIRED');
  };

  const setQuantity = (_quantity: number) => {
    setProductQuantity(_quantity);

    if (frame) {
      frame.variant.consolidatedVariants[0].quantity = _quantity;
    }
  };

  const dispatchDate = useMemo(
    () =>
      getDispatchDate({
        framed: framedByDefault(product?.framingOffered) || Boolean(frame),
        framedDispatchDate: product?.framedDispatchDate || null,
        unframedDispatchDate: product?.unframedDispatchDate || null,
        launchDate: product?.launchDate || null,
      }),
    [
      frame,
      product?.framedDispatchDate,
      product?.framingOffered,
      product?.launchDate,
      product?.unframedDispatchDate,
    ],
  );

  const collectionSetUpdateQuantity = (id: string, newQuantity: number) => {
    const newState = collectionSetProducts.map((r) =>
      r.shopifyId === id
        ? {
            ...r,
            quantity: newQuantity,
          }
        : r,
    );

    setCollectionSetProducts(newState);

    if (frame) {
      const frameConsolidatedVariantIndex =
        frame.variant.consolidatedVariants.findIndex(
          ({ shopifyProductId }) => shopifyProductId === id,
        );

      if (frameConsolidatedVariantIndex !== -1) {
        frame.variant.consolidatedVariants[
          frameConsolidatedVariantIndex
        ].quantity = newQuantity;
      }
    }
  };

  const collectionSetToggleId = (id: string) => {
    setFrame(null);

    const newState = collectionSetProducts.map((r) =>
      r.shopifyId === id
        ? {
            ...r,
            selected: !r.selected,
          }
        : r,
    );

    setCollectionSetProducts(newState);
  };

  const collectionSetToggleAll = () => {
    setFrame(null);

    const newState = collectionSetProducts.map((rr) => ({
      ...rr,
      selected: true,
    }));

    setCollectionSetProducts(newState);
  };

  return {
    loading,
    checkoutLoading,
    dispatchDate,
    product,
    frame,
    frameOptions: frameOptionsConsolidated,
    unlocked,
    unlockLoading,
    unlockMessage,
    unlockCode,
    requiresUnlock,
    quantity,
    releaseGating: options.releaseGating,
    requiresGating,
    canPurchase,
    release: options.release,
    releaseType: options.releaseType,
    setFrame,
    goToCheckout,
    unlockProduct,
    setUnlockCode,
    setQuantity,
    collectionSet: collectionSetProducts.length ? collectionSetProducts : null,
    collectionSetDescription,
    collectionSetImage,
    collectionSetToggleId,
    collectionSetToggleAll,
    collectionSetUpdateQuantity,
  };
}
